import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import Icon from '../components/Icon';
import { useBilling } from '../billing/BillingContext';
import CancelFlow from '../billing/CancelFlow';
import PricingTable from '../billing/PricingTable';
import { daysUntil, formatLongDate, formatMoney } from '../billing/format';
import UsageMeter from '../billing/UsageMeter';
import DeleteAccount from '../billing/DeleteAccount';
import '../billing/billing.css';

// One line about where the subscription stands
const statusLine = (account) => {
    if (!account?.subscribedPlan) return 'Free forever. Upgrade any time.';
    if (account.pausedUntil) return `Paused until ${formatLongDate(account.pausedUntil)}`;
    if (account.cancelAtPeriodEnd) return `Ends on ${formatLongDate(account.status === 'trialing' ? account.trialEnd : account.currentPeriodEnd)}`;
    if (account.status === 'trialing') {
        const days = daysUntil(account.trialEnd);
        return `Free trial: ${days} day${days === 1 ? '' : 's'} left (ends ${formatLongDate(account.trialEnd)})`;
    }
    return `${account.interval === 'year' ? 'Billed yearly' : 'Billed monthly'} · renews ${formatLongDate(account.currentPeriodEnd)}`;
};

const BillingScreen = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        account, usage, pricing, billingEnabled, choosePlan, buyPack, openPortal, applyAccount, refresh, busy, error, clearError,
    } = useBilling();
    const [interval, setBillingInterval] = useState('year');
    const [cancelOpen, setCancelOpen] = useState(false);
    const [notice, setNotice] = useState('');
    const [actionError, setActionError] = useState('');
    const [isWorking, setIsWorking] = useState(false);
    // ?plan=growth&interval=year: a plan picked on the landing page
    const [pickedPlan, setPickedPlan] = useState(null);

    // Back from Stripe Checkout. The webhook may land a moment after the redirect, so check a few times.
    useEffect(() => {
        const checkout = searchParams.get('checkout');
        const pack = searchParams.get('pack');
        const picked = searchParams.get('plan');
        if (picked && !checkout) {
            setPickedPlan(picked);
            if (searchParams.get('interval')) setBillingInterval(searchParams.get('interval'));
            setSearchParams({}, { replace: true });
            window.setTimeout(() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' }), 300);
            return undefined;
        }
        if (!checkout && !pack) return undefined;
        if (checkout === 'success') setNotice("You're all set. Your new plan is active.");
        if (pack === 'success') setNotice('Your extra credits are added. Keep creating.');
        setSearchParams({}, { replace: true });
        if (checkout !== 'success' && pack !== 'success') return undefined;
        let tries = 0;
        const timer = window.setInterval(() => {
            tries += 1;
            refresh();
            if (tries >= 4) window.clearInterval(timer);
        }, 2000);
        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (account?.interval) setBillingInterval(account.interval);
    }, [account?.interval]);

    const pickedPlanName = pickedPlan && !account?.subscribedPlan && pricing?.plans.find(p => p.id === pickedPlan && p.price.month > 0);

    useEffect(() => clearError, [clearError]);

    const act = async (action, message) => {
        setIsWorking(true);
        setActionError('');
        try {
            const result = await action();
            if (result?.account) applyAccount(result.account);
            setNotice(typeof message === 'function' ? message(result) : message);
        } catch (e) {
            setActionError(e.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setIsWorking(false);
        }
    };

    const plan = pricing?.plans.find(p => p.id === (account?.subscribedPlan || 'free'));
    const isPaid = Boolean(account?.subscribedPlan);

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Account</div>
                    <h1 className="ls-page-title">Plans &amp; billing</h1>
                </div>
            </div>

            {notice && (
                <div className="ls-notice" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Icon name="check_circle" size={18} /><span style={{ flex: 1 }}>{notice}</span>
                    <button type="button" className="ls-chip-remove" aria-label="Dismiss" onClick={() => setNotice('')}><Icon name="close" size={18} /></button>
                </div>
            )}
            {(actionError || error) && <div className="ls-error" role="alert"><Icon name="error" />{actionError || error}</div>}

            {account?.paymentIssue && (
                <div className="ls-alert is-danger">
                    <Icon name="credit_card_off" size={22} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>Your last payment didn't go through</div>
                        <div>We'll retry automatically. Update your card and we'll retry right away, so your weekly plan keeps running.</div>
                    </div>
                    <div className="ls-actions">
                        <button type="button" className="ls-btn ls-btn-primary" disabled={Boolean(busy)} onClick={() => openPortal('payment_method_update')}>
                            Update card
                        </button>
                        <button
                            type="button"
                            className="ls-btn ls-btn-secondary"
                            disabled={isWorking}
                            onClick={() => act(api.retryPayment, (result) => (result.paid > 0 ? 'Payment received. Thank you!' : 'Still no luck with that card. Try updating it.'))}
                        >
                            Retry now
                        </button>
                    </div>
                </div>
            )}

            {(account?.cancelAtPeriodEnd || account?.pausedUntil) && (
                <div className="ls-alert">
                    <Icon name={account.pausedUntil ? 'pause_circle' : 'event_busy'} size={22} />
                    <div style={{ flex: 1 }}>
                        {account.pausedUntil
                            ? `Your plan is paused until ${formatLongDate(account.pausedUntil)}. You're on Free limits until then.`
                            : `Your plan ends on ${formatLongDate(account.status === 'trialing' ? account.trialEnd : account.currentPeriodEnd)}. After that you'll move to Free.`}
                    </div>
                    <button type="button" className="ls-btn ls-btn-primary" disabled={isWorking} onClick={() => act(api.resumePlan, 'Welcome back. Your plan is active.')}>
                        {account.pausedUntil ? 'Resume now' : 'Keep my plan'}
                    </button>
                </div>
            )}

            <div className="ls-card ls-plan-summary">
                <div className="ls-plan-summary-head">
                    <div style={{ minWidth: 0 }}>
                        <div className="ls-label">Your plan</div>
                        <div className="ls-plan-summary-name">
                            {plan?.name || 'Free'}
                            {account?.discount && <span className="ls-plan-tag">{account.discount.name || '50% off'}</span>}
                        </div>
                        <div className="ls-card-sub">{statusLine(account)}</div>
                    </div>
                    <div className="ls-actions">
                        {account?.canManagePayment && (
                            <button type="button" className="ls-btn ls-btn-secondary" disabled={Boolean(busy)} onClick={() => openPortal()}>
                                {busy === 'portal' ? <span className="ls-spinner" /> : <><Icon name="credit_card" size={18} />Payment &amp; invoices</>}
                            </button>
                        )}
                        {!isPaid && (
                            <button type="button" className="ls-btn ls-btn-primary" onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}>
                                <Icon name="rocket_launch" size={18} />Upgrade
                            </button>
                        )}
                    </div>
                </div>
                {usage ? (
                    <div className="ls-meters">
                        <UsageMeter label="Generations" {...usage.generations} />
                        <UsageMeter label="AI videos" {...usage.videos} />
                        <UsageMeter label="Products" used={usage.products.used} limit={usage.products.limit} neutral />
                    </div>
                ) : (
                    <div className="ls-skeleton" style={{ height: 56 }} />
                )}
                <div className="ls-plan-summary-foot">
                    <span>
                        {usage?.resetsAt && `Monthly allowances reset ${formatLongDate(usage.resetsAt)}. Extra credits never expire.`}
                        {!billingEnabled && account && ' Checkout is switched off on this server.'}
                    </span>
                    {isPaid && !account.cancelAtPeriodEnd && (
                        <button type="button" className="ls-link is-muted" onClick={() => setCancelOpen(true)}>Cancel plan</button>
                    )}
                </div>
            </div>

            <div id="plans" className="ls-plans-heading">
                <h2>{isPaid ? 'Change plan' : 'Get more out of every week'}</h2>
                <p>Pick the plan for how much you want to post. One new customer a month pays for Growth.</p>
                <p className="ls-section-fine">
                    Plans renew automatically until you cancel. By subscribing you agree to the{' '}
                    <Link to="/terms#plans">Terms of Service</Link>.
                </p>
            </div>
            {pickedPlanName && (
                <div className="ls-notice" style={{ textAlign: 'center' }}>
                    {pickedPlanName.trialDays && account?.trialAvailable
                        ? `Start your ${pickedPlanName.trialDays}-day ${pickedPlanName.name} trial below. No charge until it ends, and we'll email you first.`
                        : `Get ${pickedPlanName.name} below.`}
                </div>
            )}
            <PricingTable
                pricing={pricing}
                recommended={pickedPlanName ? pickedPlan : undefined}
                interval={interval}
                onIntervalChange={setBillingInterval}
                onSelect={(planId, planInterval) => choosePlan(planId, planInterval).then(result => result?.account && setNotice('Your plan is updated.'))}
                account={account}
                busy={busy}
            />

            {pricing?.packs?.length > 0 && (
                <div className="ls-card">
                    <div>
                        <div className="ls-card-title">Running low? Top up</div>
                        <div className="ls-card-sub" style={{ marginTop: 2 }}>One-off, on any plan. Credits never expire.</div>
                    </div>
                    <div className="ls-packs">
                        {pricing.packs.map(pack => (
                            <div key={pack.id} className="ls-pack">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{pack.name}</div>
                                    <div className="ls-muted" style={{ fontSize: 13 }}>{pack.outcome}</div>
                                </div>
                                <button type="button" className="ls-btn ls-btn-secondary" disabled={Boolean(busy)} onClick={() => buyPack(pack.id)}>
                                    {busy === `pack:${pack.id}` ? <span className="ls-spinner" /> : formatMoney(pack.price, pricing.currency)}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <DeleteAccount />

            {cancelOpen && <CancelFlow onClose={() => setCancelOpen(false)} />}
        </>
    );
};

export default BillingScreen;
